import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import { CheckCircle2, XCircle, ArrowRight, Layers, Clock3, CheckCheck, Ban, Undo2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import "../Trainer/TrainerDashboard.css";
import "../common/lms-pages.css";
import {
  getBatchChangeRequests,
  approveBatchChangeRequest,
  rejectBatchChangeRequest,
  revertBatchChangeRequest,
  extractBatchChangeErrorMessage,
} from "../../../services/BatchChangeRequestService";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";
import { Skeleton } from "../common/Skeleton/Skeleton.jsx";
import { confirm as confirmDialog } from '../common/Confirm/confirm';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone.jsx';

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "reverted", label: "Reverted" },
];

const STATUS_META = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  reverted: "Reverted",
};

// Colour is a tag here, not the subject: the pill says where a request
// stands without the reviewer having to read the word.
const STATUS_TONE = {
  pending: "pg-pill--warn",
  approved: "pg-pill--good",
  rejected: "pg-pill--bad",
  cancelled: "",
  reverted: "pg-pill--live",
};

const initials = (name) =>
  (name || "?").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

const BatchRequestsList = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [details, setDetails] = useState(null);
  // Approve / reject / revert from the popup is confirmed in it (the done
  // panel), which then closes itself.
  const { done } = usePopupDone(Boolean(details));
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [actingId, setActingId] = useState(null);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0, reverted: 0 });
  // Until the first counts arrive the figures shimmer: a 0 there would read
  // as "no requests" rather than "not loaded yet".
  const [countsReady, setCountsReady] = useState(false);
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Module `batch-requests`: named after the route, like every other page.
  const canView = hasPermission("batch-requests", "view", "lms");
  // Acting on a request -- approve, reject, revert, re-approve -- is `update` on the module. The system has no
  // approve/reject operations to grant, so checking for them hid the
  // buttons from everyone but a super admin. The API checks the same.
  const canApprove = hasPermission("batch-requests", "update", "lms");
  const canReject = canApprove;

  // Permission check
  useEffect(() => {
    if (!canView) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view batch change requests.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [canView, navigate]);

  const fetchData = useCallback(async () => {
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await getBatchChangeRequests({
        page: currentPage,
        limit: recordsPerPage,
        search: searchTerm,
        searchField,
        status: statusFilter,
        fromDate,
        toDate,
      });
      const pageOffset = (currentPage - 1) * recordsPerPage;
      const rows = (res.data || []).map((r, index) => ({ ...r, srno: pageOffset + index + 1 }));
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...rows.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(rows);
      }
      appendRef.current = false;
      setTotalRecords(res.pagination?.total || 0);
      // The tiles and tab badges arrive with the list -- one request, not
      // one more per status.
      if (res.counts) setCounts(res.counts);
    } catch (error) {
      console.error("Error fetching batch change requests:", error);
      toast.error(extractBatchChangeErrorMessage(error, "Failed to fetch batch change requests. Please try again."));
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      // Settled either way, so a failed load shows 0 rather than the tiles
      // shimmering for ever.
      setCountsReady(true);
    }
  }, [currentPage, recordsPerPage, searchTerm, searchField, statusFilter, fromDate, toDate]);

  useEffect(() => {
    if (canView) fetchData();
  }, [fetchData, canView]);

  // The counts come back with the list, so a refresh is one request.
  const refreshAll = () => { fetchData(); };

  const handleStatusTab = (value) => {
    appendRef.current = false;
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (n) => {
    appendRef.current = false;
    setRecordsPerPage(n);
    setCurrentPage(1);
  };

  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSearchField(field);
    runSearch(value);
  };

  const handleApprove = async (row) => {
    const isReapprove = row.status === "reverted";
    const confirm = await confirmDialog({
      title: isReapprove ? "Re-approve this batch change?" : "Approve this batch change?",
      html: `Move <b>${row.studentName || "this student"}</b> from <b>${row.fromBatch?.name || "—"}</b> to <b>${row.toBatch?.name || "—"}</b>?`,
      confirmButtonText: isReapprove ? "Yes, re-approve" : "Yes, approve",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setActingId(row.id);
    try {
      await approveBatchChangeRequest(row.id);
      toast.success("The student has been moved to the new batch.");
      refreshAll();
    } catch (error) {
      toast.error(extractBatchChangeErrorMessage(error, "Please try again."));
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (row) => {
    const { value: rejectionReason, isConfirmed } = await confirmDialog({
      title: "Reject this request?",
      input: "textarea",
      inputLabel: "Reason (optional, shown to the student)",
      inputPlaceholder: "Why is this being rejected?",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      tone: "danger",
    });
    if (!isConfirmed) return;

    setActingId(row.id);
    try {
      await rejectBatchChangeRequest(row.id, rejectionReason || "");
      toast.success("Rejected");
      refreshAll();
    } catch (error) {
      toast.error(extractBatchChangeErrorMessage(error, "Please try again."));
    } finally {
      setActingId(null);
    }
  };

  /** Undo a mistaken approval — swaps the student back to their original batch. */
  const handleRevert = async (row) => {
    const confirm = await confirmDialog({
      title: "Revert this approval?",
      html: `Move <b>${row.studentName || "this student"}</b> back from <b>${row.toBatch?.name || "—"}</b> to <b>${row.fromBatch?.name || "—"}</b>? This undoes the approval.`,
      confirmButtonText: "Yes, revert it",
      cancelButtonText: "Keep as approved",
    });
    if (!confirm.isConfirmed) return;

    setActingId(row.id);
    try {
      await revertBatchChangeRequest(row.id);
      toast.success("The student has been moved back to their original batch.");
      refreshAll();
    } catch (error) {
      toast.error(extractBatchChangeErrorMessage(error, "Please try again."));
    } finally {
      setActingId(null);
    }
  };

  const headers = [
    {
      title: "Student",
      prop: "studentName",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.studentName || "Unnamed"}</span>
          {row.studentEmail && (
            <span className="lp-sub">{row.studentEmail}</span>
          )}
        </div>
      ),
    },
    {
      title: "Course",
      prop: "courseName",
      cell: (row) => <span className="lp-plain">{row.courseName || "—"}</span>,
    },
    {
      title: "Change",
      prop: "batchChange",
      cell: (row) => (
        <span className="bq-move">
          <span className="bq-move-from">{row.fromBatch?.name || "—"}</span>
          <ArrowRight size={13} aria-hidden="true" />
          <span className="bq-move-to">{row.toBatch?.name || "—"}</span>
        </span>
      ),
    },
    {
      title: "Requested",
      prop: "createdAt",
      sortable: true,
      cell: (row) => (
        <span className="lp-when">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      sortable: true,
      cell: (row) => (
        <span className={`pg-pill ${STATUS_TONE[row.status] || ""}`.trim()}>
          {STATUS_META[row.status] || row.status}
        </span>
      ),
    },
  ];

  // Every field picked here is honored server-side by the same `search` +
  // `searchField` convention the rest of the app uses — "All fields"
  // searches student name/email, batch, and reason together via OR;
  // picking one of these scopes the match to exactly that field instead.
  const searchableFields = [
    { value: "all", label: "All fields" },
    { value: "studentName", label: "Student name" },
    { value: "studentEmail", label: "Email" },
    { value: "batch", label: "Batch" },
    { value: "reason", label: "Reason" },
  ];

  if (!canView) return null;

  // What a request can have done to it, given where it is and who is asking.
  const actionsFor = (row) => {
    if (!row) return [];
    if (row.status === "pending") {
      return [
        canApprove && { key: "approve", label: "Approve", run: handleApprove },
        canReject && { key: "reject", label: "Reject", run: handleReject, danger: true },
      ].filter(Boolean);
    }
    // Revert and re-approve are the same approve-tier action in reverse.
    if (row.status === "approved") {
      return canApprove
        ? [{ key: "revert", label: "Revert", run: handleRevert, danger: true }]
        : [];
    }
    if (row.status === "reverted") {
      return canApprove
        ? [{ key: "reapprove", label: "Re-approve", run: handleApprove }]
        : [];
    }
    return [];
  };

  const detailActions = actionsFor(details);

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          <span className="lp-count">
            {countsReady ? (
              `${counts.all} ${counts.all === 1 ? "request" : "requests"}`
            ) : (
              <Skeleton shape="pill" />
            )}
          </span>
        </div>
      </header>

      {/* The readout: where the queue stands. Label above figure. */}
      <div className="br-stats">
        {[
          { icon: Layers, label: "Total requests", value: counts.all },
          { icon: Clock3, label: "Awaiting review", value: counts.pending, tone: "br-stat--warn" },
          { icon: CheckCheck, label: "Approved", value: counts.approved, tone: "br-stat--good" },
          { icon: Ban, label: "Rejected", value: counts.rejected, tone: "br-stat--bad" },
          { icon: Undo2, label: "Reverted", value: counts.reverted },
        ].map(({ icon: Icon, label, value, tone }) => (
          <div className={`br-stat ${tone || ""}`.trim()} key={label}>
            <span className="br-stat-label">
              <Icon size={13} aria-hidden="true" />
              {label}
            </span>
            <span className="br-stat-value">
              {countsReady ? value : <Skeleton shape="title" width="40" />}
            </span>
          </div>
        ))}
      </div>

      <FilterSheet label="Filter requests">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="bq-search">
              Search
            </label>
            <input
              id="bq-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by student, email, batch or reason..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="bq-field">
              Search in
            </label>
            <select
              id="bq-field"
              className="pg-select"
              value={searchField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="bq-rows">
              Rows per load
            </label>
            <select
              id="bq-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label" htmlFor="bq-from">
                Requested from
              </label>
              <input
                id="bq-from"
                type="date"
                className="pg-input"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  appendRef.current = false;
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="bq-to">
                Requested to
              </label>
              <input
                id="bq-to"
                type="date"
                className="pg-input"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => {
                  appendRef.current = false;
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn"
              disabled={!fromDate && !toDate}
              onClick={() => {
                appendRef.current = false;
                setFromDate("");
                setToDate("");
                setCurrentPage(1);
              }}
            >
              Clear dates
            </button>
          </div>
        </div>

        {/* Status is a segmented control rather than a sixth dropdown: it is
            the filter this page is actually read through. */}
        <div className="bq-seg" role="group" aria-label="Filter by status">
          {STATUS_TABS.map((tab) => {
            const on = statusFilter === tab.value;
            return (
              <button
                key={tab.value || "all"}
                type="button"
                aria-pressed={on}
                className={`bq-seg-btn${on ? " is-on" : ""}`}
                onClick={() => handleStatusTab(tab.value)}
              >
                {tab.label}
                {/* The badge waits for the count rather than showing 0. */}
                {countsReady && (
                  <span className="bq-seg-n">
                    {tab.value ? counts[tab.value] : counts.all}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more requests...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} requests
          </span>
        ) : totalRecords > 0 ? (
          <span>
            All {totalRecords} request{totalRecords === 1 ? "" : "s"} loaded
          </span>
        ) : (
          !loading && <span>No request matches those filters.</span>
        )}
      </div>

      {/* One request, in full. The reason is the thing a reviewer is
          actually deciding on, so it gets room here rather than a
          truncated column. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bq-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Batch change request</span>
                <h3 className="pg-modal-title" id="bq-title">
                  {details.studentName || "Unnamed"}
                </h3>
                {details.studentEmail && (
                  <p className="pg-modal-sub">{details.studentEmail}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {done ? (
                <PopupDone message={done} onClose={() => setDetails(null)} />
              ) : (
              <>
              {/* The move itself, said once and large -- it is what the
                  request is. */}
              <div className="bq-move bq-move--lg">
                <span className="bq-move-from">
                  {details.fromBatch?.name || "—"}
                </span>
                <ArrowRight size={18} aria-hidden="true" />
                <span className="bq-move-to">
                  {details.toBatch?.name || "—"}
                </span>
              </div>

              <dl className="quiz-facts">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`pg-pill ${STATUS_TONE[details.status] || ""}`.trim()}
                    >
                      {STATUS_META[details.status] || details.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Course</dt>
                  <dd>{details.courseName || "—"}</dd>
                </div>
                <div>
                  <dt>Requested</dt>
                  <dd>
                    {details.createdAt
                      ? new Date(details.createdAt).toLocaleString()
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Last updated</dt>
                  <dd>
                    {details.updatedAt
                      ? new Date(details.updatedAt).toLocaleString()
                      : "—"}
                  </dd>
                </div>
              </dl>

              <h5 className="pgm-section-title">Reason</h5>
              <p className="lp-prose">
                {details.reason || "No reason was given."}
              </p>
              </>
              )}
            </div>

            {!done && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {detailActions.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className={`pgm-btn${a.danger ? " pgm-btn--danger" : ""}`}
                    disabled={actingId === details.id}
                    onClick={() => {
                      // The popup stays up; a success shows its done panel.
                      const row = details;
                      a.run(row);
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchRequestsList;
